import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Preferences } from '@capacitor/preferences';

type AppLanguage = 'de' | 'en';

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  private readonly storageKey = 'language';
  private currentLang: AppLanguage = 'de';

  constructor(private translate: TranslateService) {
    this.translate.addLangs(['de', 'en']);
    this.translate.setDefaultLang('de');
  }

  async init(): Promise<void> {
    const { value } = await Preferences.get({ key: this.storageKey });
    const stored = this.isSupportedLanguage(value) ? value : null;
    const browserLang = this.translate.getBrowserLang();
    const fallback = this.isSupportedLanguage(browserLang) ? browserLang : 'de';
    const lang = (stored ?? fallback) as AppLanguage;
    await this.use(lang);
  }

  getCurrentLang(): AppLanguage {
    return (this.translate.currentLang as AppLanguage) || this.currentLang;
  }

  async use(lang: AppLanguage): Promise<void> {
    this.currentLang = lang;
    this.translate.use(lang);
    await Preferences.set({ key: this.storageKey, value: lang });
  }

  instant(key: string, interpolateParams?: Record<string, unknown>): string {
    return this.translate.instant(key, interpolateParams);
  }

  private isSupportedLanguage(lang: string | null): lang is AppLanguage {
    return lang === 'de' || lang === 'en';
  }
}
